import { useState } from 'react'; // Removed useEffect/location since no token_hash
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { FormEvent } from 'react';
import { Building2 } from 'lucide-react';

export default function ResetPortalPassword() {
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const handleVerifyOtp = async (e: FormEvent) => {
    e.preventDefault();
    setMessage('');
    setError('');
    setIsSubmitting(true);

    try {
      const { data, error: verifyError } = await supabase.auth.verifyOtp({
        email,
        token: otp,
        type: 'recovery',
      });

      if (verifyError) {
        console.error('OTP verification error:', verifyError);
        setError(
          verifyError.message ||
            'Invalid or expired code. Please try again or request a new one.'
        );
        return;
      }

      if (data.session) {
        setIsVerified(true);
        setMessage('Code verified! Set your new password.');
      } else {
        setError('Unable to verify code. Please try again.');
      }
    } catch (err) {
      console.error('Unexpected verification error:', err);
      setError('An unexpected error occurred during verification.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdatePassword = async (e: FormEvent) => {
    e.preventDefault();
    setMessage('');
    setError('');
    setIsSubmitting(true);

    try {
      const { data, error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (updateError) {
        console.error('Update password error:', updateError);
        setError(updateError.message);
        return;
      }

      console.log('Password update successful:', data);
      setMessage('Password updated successfully! Redirecting to login...');
      setTimeout(() => navigate('/portal/auth'), 2000);
    } catch (err) {
      console.error('Unexpected error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className='min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-muted/30 to-background p-4'>
      <div className='w-full max-w-md space-y-8'>
        {/* Logo and Header */}
        <div className='text-center space-y-4'>
          <div className='mx-auto w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center'>
            <Building2 className='w-6 h-6 text-primary' />
          </div>
          <p className='text-muted-foreground'>
            Power your B2B voice prospecting with AI
          </p>
        </div>
        {/* Password Reset Form */}
        <Card className='border-2'>
          <CardHeader className='space-y-1'>
            <CardTitle className='text-2xl font-semibold'>
              Reset Password
            </CardTitle>
            <CardDescription>
              Enter your email and the code from your email
            </CardDescription>
          </CardHeader>
          <CardContent>
            {message && (
              <Alert variant='default' className='mb-4'>
                <AlertDescription>{message}</AlertDescription>
              </Alert>
            )}
            {error && (
              <Alert variant='destructive' className='mb-4'>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            {!isVerified ? (
              <form onSubmit={handleVerifyOtp} className='space-y-4'>
                <div className='space-y-2'>
                  <Label htmlFor='email'>Email</Label>
                  <Input
                    id='email'
                    type='email'
                    placeholder='your@email.com'
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className='h-11'
                    disabled={isSubmitting}
                  />
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='otp'>Reset Code</Label>
                  <Input
                    id='otp'
                    type='text'
                    placeholder='Enter 6-digit code'
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    required
                    className='h-11'
                    disabled={isSubmitting}
                  />
                </div>
                <Button
                  type='submit'
                  className='w-full h-11'
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Verifying...' : 'Verify Code'}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleUpdatePassword} className='space-y-4'>
                <div className='space-y-2'>
                  <Label htmlFor='new-password'>New Password</Label>
                  <Input
                    id='new-password'
                    type='password'
                    placeholder='Enter new password'
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    className='h-11'
                    disabled={isSubmitting}
                  />
                </div>
                <Button
                  type='submit'
                  className='w-full h-11'
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Updating...' : 'Update Password'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
